import {
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
  type ElementType,
  type JSX,
} from 'react';

/* eslint-disable-next-line import/order */
import { type BaseProps, type OverridableTypeMap } from '@mui/material/OverridableComponent';

/* eslint-disable-next-line no-restricted-imports */
import { type StackTypeMap as RootStackTypeMap } from '@mui/material/Stack';

import { Stack } from './Stack';

export type StackDefaultComponent = RootStackTypeMap['defaultComponent'];

type StackTypeMap<E extends ElementType> = RootStackTypeMap<{ component?: E }, E>;

type OmitableStackProp<E extends ElementType, T extends OverridableTypeMap> =
  | keyof BaseProps<T>
  | keyof ComponentPropsWithoutRef<E>;

/**
 * Represents the root MUI props for the {@link PolymorphicStack} component.
 *
 * This type must be generated by distributing over the union {@link T}, which is a type map that
 * already distributes over the union {@link E}.
 *
 * This type is analogous to Material UI's {@link OverrideProps} type, with the one exception being
 * the omission of the types from the {@link BaseProps<T>} shown below.  This omission has to be
 * performed on the {@link BaseProps<T>}, instead of on the overall resulting type
 * {@link StackProps}, due to Material UI internal type definitions related to the types used below.
 *
 * @see https://mui.com/material-ui/guides/composition/#generic for more information related to the
 * extension of generically typed Material UI components.
 */
type StackProps<
  E extends ElementType,
  T extends OverridableTypeMap,
  O extends OmitableStackProp<E, T> = never,
> = T extends OverridableTypeMap
  ? Omit<BaseProps<T>, O> & Omit<ComponentPropsWithoutRef<E>, keyof BaseProps<T> | O>
  : never;

export type PolymorphicStackProps<
  E extends ElementType = StackDefaultComponent,
  O extends OmitableStackProp<E, StackTypeMap<E>> = never,
> = StackProps<E, StackTypeMap<E>, O>;

/**
 * An internalized form of the MUI {@link RootStack} component that is typed in a manner that allows
 * it to be more easily extended and used as the base component that other components may wrap.
 *
 * This component, and it's associated types, should be used when the {@link RootStack} is being
 * wrapped when creating new components.
 *
 * @example
 * ```tsx
 * import { Stack, StackProps, StackDefaultComponent } from 'components-v2/structural/Stack';
 *
 * type MyComponentProps<E extends ElementType = StackDefaultComponent> = StackProps<E> & {
 *   readonly myCustomProp: string;
 * }
 *
 * const MyComponent = <E extends ElementType = StackDefaultComponent>(
 *   props: MyComponentProps<E>
 * ) => (
 *   <Stack {...props}>
 *     <Typography>My Custom Component</Typography>
 *     {props.children}
 *   </Stack>
 * )
 * ```
 */
export const PolymorphicStack = <E extends ElementType = StackDefaultComponent>({
  ref,
  ...props
}: {
  readonly ref?: ComponentPropsWithRef<E>['ref'];
} & PolymorphicStackProps<E>): JSX.Element => (
  <Stack {...props} ref={ref}>
    {props.children}
  </Stack>
);
